import React, { useState, useMemo, useCallback, memo, Suspense, lazy, Component, useEffect, useRef } from 'react';
import {
    Box,
    Typography,
    CircularProgress,
    LinearProgress,
    Alert,
    Button,
    Chip,
    Divider,
    Snackbar,
    useMediaQuery,
    useTheme,
    Menu,
    MenuItem,
} from '@mui/material';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import PieChartOutlineOutlinedIcon from '@mui/icons-material/PieChartOutlineOutlined';
import PersonSearchOutlinedIcon    from '@mui/icons-material/PersonSearchOutlined';
import CollectionsOutlinedIcon     from '@mui/icons-material/CollectionsOutlined';
import Refresh from '@mui/icons-material/Refresh';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import { motion, AnimatePresence } from 'framer-motion';

import { estimateReach } from './estimateReach';
import { useQueryClient, useQuery } from '@tanstack/react-query';
import { useFetchReportWithPolling, useFetchReportSnapshots, useFetchRefreshInfo, useFetchReportStatus } from '../../react-query/hooks/useFetchReport';
import reportServices from '../../react-query/services/reportServices';
import paymentportalservice from '../../services/paymentportalservice';
import DashboardHeaderSection from '../common/dashboardheader/DashboardHeader';
import PublicReportHeader    from './PublicReportHeader';
import RefreshInfoBar        from './RefreshInfoBar';
import AddManualLinksModal   from './components/AddManualLinksModal';
import CampaignPickerModal   from './CampaignPickerModal';
import AttachResultModal     from './AttachResultModal';
import GetCookieByName       from '../../hooks/GetCookie';
import AddLinkIcon           from '@mui/icons-material/AddLink';

// true when the visitor has no valid auth cookie (shared / public view) —
// exported for reuse by ReportGroupDashboard.jsx, which needs the same check
// for a group's own shareable-link dashboard.
export function checkIsPublicView() {
    try {
        const raw = GetCookieByName('auth');
        if (!raw || typeof raw !== 'string') return true;
        const parsed = JSON.parse(raw);
        return !parsed?.token;
    } catch {
        return true;
    }
}

const DashboardTab   = lazy(() => import('./components/DashboardTab'));
const InfluencersTab = lazy(() => import('./components/InfluencersTab'));
const ContentsTab    = lazy(() => import('./components/ContentsTab'));

// ── Global design tokens ──────────────────────────────────────────────────────
const C = {
    orange:    '#FF5200',
    purple:    '#7C6FF7',
    bg:        '#EDEEF8',      // light lavender matching the influencer discovery page
    surface:   '#FFFFFF',
    border:    '#E8E9ED',
    text:      '#0B0B0B',
    textSub:   '#52514E',
    textMuted: '#898781',
};

// ── Framer variants ───────────────────────────────────────────────────────────
const pageIn = {
    hidden: { opacity: 0 },
    show:   { opacity: 1, transition: { duration: 0.4 } },
};

// ── Error boundary ────────────────────────────────────────────────────────────
class ReportErrorBoundary extends Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, error: null };
    }
    static getDerivedStateFromError(error) { return { hasError: true, error }; }
    componentDidCatch(error, info) { console.error('[ReportDashboard]', error, info.componentStack); }
    render() {
        if (this.state.hasError) return (
            <>
                <DashboardHeaderSection hideSearch />
                <Box width={{ xs: '100%', lg: 'calc(100% - 80px)' }} marginLeft={{ xs: '0px', lg: '80px' }} marginTop={8} sx={{ p: 3 }}>
                    <Alert severity="error" action={<Button color="inherit" size="small" startIcon={<Refresh />} onClick={() => { this.setState({ hasError: false }); window.location.reload(); }}>Reload</Button>}>
                        <Typography variant="body2" fontWeight={600}>Something went wrong loading this report.</Typography>
                        <Typography variant="caption" display="block" mt={0.5}>{this.state.error?.message}</Typography>
                    </Alert>
                </Box>
            </>
        );
        return this.props.children;
    }
}

// ── Utilities ─────────────────────────────────────────────────────────────────
function fmt(n) {
    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}m`;
    if (n >= 1_000)     return `${(n / 1_000).toFixed(2)}k`;
    return String(n);
}
function extractCaption(val) {
    if (!val) return '';
    if (typeof val === 'string') return val;
    if (typeof val === 'object') return val.text ?? '';
    return String(val);
}
function proxyImgUrl(url) {
    if (!url) return null;
    if (!url.includes('cdninstagram.com')) return url;
    return `${process.env.REACT_APP_API_URL}report/proxy-image?url=${encodeURIComponent(url)}`;
}
// Triggers a real download with no new tab/window — the standard technique. The S3
// signed URL already carries a Content-Disposition: attachment header (see
// getExportDownloadUrl in s3ExportCache.js), so this works reliably even though the
// URL is cross-origin: the browser downloads it in place rather than navigating.
function triggerFileDownload(url, filename) {
    const link = document.createElement('a');
    link.href = url;
    if (filename) link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
}
function stringToGradient(str) {
    let hash = 0;
    for (let i = 0; i < (str || '').length; i++) hash = (str || '').charCodeAt(i) + ((hash << 5) - hash);
    const h1 = Math.abs(hash) % 360;
    const h2 = (h1 + 45) % 360;
    return `linear-gradient(160deg, hsl(${h1},55%,18%) 0%, hsl(${h2},65%,28%) 100%)`;
}
// Media types whose Shares can be set by hand — Carousels only (Images were
// dropped after product review). Mirrors SHARES_EDITABLE_MEDIA_TYPES in
// pluginbackendnode/routes/reports/common.js (PATCH /:id/post-shares rejects the rest).
const SHARES_EDITABLE_TYPES = new Set(['Carousels']);
function mapInfluencerForTable(inf, { totalPayment = 0, influencerCount = 0, creatorPosts = null } = {}) {
    const totalEng  = (inf.totalLikes || 0) + (inf.totalComments || 0) + (inf.totalReshares || 0) + (inf.totalSaves || 0) + (inf.totalReposts || 0);
    const views     = inf.totalViews || 0;
    // Reels: ER by views; Images: ER by followers (fallback)
    const erDenom   = views > 0 ? views : (inf.followerCount || 0);
    const er        = totalEng > 0 && erDenom > 0 ? `${((totalEng / erDenom) * 100).toFixed(2)} %` : 'NA';
    const costNum   = Number(inf.payableAmount) || 0;
    // If no individual cost is set but only 1 influencer exists with a known budget, use the total budget
    const effectiveCost = costNum > 0 ? costNum
        : (totalPayment > 0 && influencerCount === 1 ? totalPayment : 0);
    const fmtRupee  = (n) => n >= 100 ? `₹${Math.round(n).toLocaleString('en-IN')}` : n >= 1 ? `₹${n.toFixed(2)}` : n >= 0.01 ? `₹${n.toFixed(3)}` : `₹${n.toFixed(4)}`;
    const cpe       = effectiveCost > 0 && totalEng > 0 ? fmtRupee(effectiveCost / totalEng) : 'NA';
    // CPV is only meaningful for video/reel posts; NA for image posts
    const cpv       = effectiveCost > 0 && views > 0 ? fmtRupee(effectiveCost / views) : 'NA';
    const posts = inf.postCount || 1;
    const YT_CHANNEL_ID_RE = /^UC[a-zA-Z0-9_-]{22}$/;
    // Shares are stored per post, so the aggregate cell is only editable when it IS
    // one post's value: a single Carousel. Callers that don't pass `creatorPosts`
    // (e.g. the report-group rollup) get a read-only cell.
    const sharesEditLink = creatorPosts?.length === 1 && SHARES_EDITABLE_TYPES.has(creatorPosts[0].mediaType)
        ? creatorPosts[0].link : null;
    // Derive platform defensively — old records pre-date the `platform` field.
    // Fall back to detecting a YouTube channel ID in pk or username.
    const derivedPlatform = inf.platform
        || (YT_CHANNEL_ID_RE.test(inf.pk       || '') ? 'youtube' : null)
        || (YT_CHANNEL_ID_RE.test(inf.username  || '') ? 'youtube' : null);
    return {
        username: inf.username,
        pk:       inf.pk || null,
        platform: derivedPlatform,
        name: inf.fullName || inf.username, handle: '@' + inf.username, score: '—',
        avatar: proxyImgUrl(inf.profilePicUrl) || null,
        // Influencer's stored location (city/state/country), enriched server-side from
        // RapidInfluencer (see enrichLocations in pluginbackendnode/routes/report.js).
        // Most report-only influencers have none — render blank, not 'NA', when absent.
        zone: inf.zone || '',
        // Per-campaign attribution code, scoped to this report (not the influencer
        // globally) — see enrichRetailerCodes in pluginbackendnode/routes/report.js.
        // Only ever shown as a column for Xiaomi-brand reports (InfluencersTab), but
        // the value itself is harmless to carry through regardless of brand.
        retailerCode: inf.retailerCode || '',
        followers: inf.followerCount || 0,
        deliverables: `${posts} of ${posts}`, trackingLink: 'NA',
        totalCost: (() => {
          const n = Number(inf.payableAmount);
          return inf.payableAmount && !isNaN(n) && n > 0
            ? `₹${n.toLocaleString('en-IN')}`
            : 'NA';
        })(),
        rawBudget: costNum,
        rawTotalEng: totalEng,
        rawTotalViews: views,
        likes: fmt(inf.totalLikes || 0), comments: inf.totalComments || 0,
        shares: fmt(inf.totalReshares || 0), reposts: fmt(inf.totalReposts || 0),
        rawShares: inf.totalReshares || 0, sharesEditLink,
        reelViews: inf.viewsEstimated ? `~${fmt(views)}` : fmt(views),
        estimatedReach: fmt(estimateReach(views)),
        engagementRate: er, cpe, cpv,
        postType: inf.dominantPostType || 'Reels',
        contentsPosted: posts === 1 ? '1 Post' : `${posts} Posts`,
    };
}
// Manual stat overrides keyed by report id.
const STAT_OVERRIDES = {
    '6ac7616b2c713476f526e4ce': { campaignViews: 24_200_000, totalLikes: 646_932 },
};

function computeStats(report) {
    const override = STAT_OVERRIDES[report?._id] || {};
    const posts          = report?.posts ?? [];
    const processed      = posts.filter(p => p.apiHit);
    const total          = posts.length;
    const processedCount = processed.length;
    const isProcessing   = total > 0 && processedCount < total;
    const isEmpty        = total === 0;
    const totalLikes     = override.totalLikes ?? processed.reduce((s, p) => s + (p.like_count    || 0), 0);
    const totalComments  = processed.reduce((s, p) => s + (p.comment_count || 0), 0);
    const totalReshares  = processed.reduce((s, p) => s + (p.reshare_count || 0), 0);
    const totalSaves     = processed.reduce((s, p) => s + (p.save_count    || 0), 0);
    const totalReposts   = processed.reduce((s, p) => s + (p.repost_count  || 0), 0);
    const totalViews     = override.campaignViews ?? processed.reduce((s, p) => s + (p.views         || 0), 0);
    const totalPlays     = override.campaignViews ?? processed.reduce((s, p) => s + (p.totalPlays    || p.views || 0), 0);
    const totalFbPlays   = override.campaignViews != null ? 0 : processed.reduce((s, p) => s + (p.fbPlays       || 0), 0);
    const totalEngagement = totalLikes + totalComments + totalReshares + totalSaves + totalReposts;
    const paidPartnershipCount = processed.filter(p => p.isPaidPartnership).length;
    const hiddenLikesCount     = processed.filter(p => p.likesHidden).length;
    const reelViews      = totalViews || totalPlays;
    // ER denominator: views for reels, total followers for image-only campaigns
    const totalFollowers = (report?.influencers || []).reduce((s, inf) => s + (inf.followerCount || 0), 0);
    const erDenominator  = reelViews || totalFollowers;
    // Campaign Views = sum of the per-influencer "Views" column shown in the
    // Influencers tab (influencer.totalViews already blends real + estimated
    // views — see buildInfluencers on the backend), not the raw post-level
    // totalViews/totalPlays above.
    const campaignViews  = override.campaignViews ?? (report?.influencers || []).reduce((s, inf) => s + (inf.totalViews || 0), 0);
    const engagementRate = totalEngagement > 0 && erDenominator > 0
        ? `${((totalEngagement / erDenominator) * 100).toFixed(2)} %` : 'NA';
    // totalPayment is the manually saved budget; use it for CPE/CPV if set
    const effectiveBudget = Number(report?.totalPayment) || 0;
    const budgetSpent = effectiveBudget > 0
        ? `₹${effectiveBudget.toLocaleString('en-IN')}` : 'NA';
    const cpe = effectiveBudget > 0 && totalEngagement > 0
        ? `₹${(effectiveBudget / totalEngagement).toFixed(2)}` : 'NA';
    const cpv = effectiveBudget > 0 && reelViews > 0
        ? `₹${(effectiveBudget / reelViews).toFixed(2)}` : 'NA';
    const isStatsFailed = processedCount > 0 && processedCount === total && totalLikes === 0 && totalComments === 0 && totalViews === 0;
    const liveInfluencerCount = report?.influencers?.length
        ?? new Set(processed.filter(p => p.creator?.username).map(p => p.creator.username)).size;
    return {
        isEmpty, isProcessing, isStatsFailed, statsOverridden: Boolean(override.campaignViews), processedCount, total,
        percentage: total > 0 ? Math.round((processedCount / total) * 100) : 0,
        influencerCount: report?.influencerCount ?? 0, liveInfluencerCount,
        postsLive: processedCount,
        campaignViews: fmt(campaignViews),
        campaignViewsRaw: campaignViews,
        estimatedReach: fmt(estimateReach(campaignViews)),
        budgetSpent,
        totalEngagement: fmt(totalEngagement), totalLikes, totalComments, totalReshares,
        totalSaves, totalReposts, totalViews, totalPlays, totalFbPlays, engagementRate, cpe, cpv,
        paidPartnershipCount, hiddenLikesCount,
    };
}

// ── Processing banner ─────────────────────────────────────────────────────────
const ProcessingBanner = memo(({ processedCount, total, percentage, onRetry, retrying, isFailed, isPublic }) => (
    <Alert
        severity={isFailed ? 'warning' : 'info'}
        sx={{ mb: 2.5, borderRadius: 2, bgcolor: '#FFF8F5', border: `1px solid rgba(255,82,0,0.2)`, color: C.text,
            '& .MuiAlert-icon': { color: C.orange } }}
        icon={retrying ? <CircularProgress size={18} thickness={5} sx={{ color: C.orange }} /> : undefined}
        action={!retrying && !isPublic ? <Button size="small" onClick={onRetry} sx={{ background: 'linear-gradient(135deg, #FF5200 0%, #FF8C00 100%)', color: '#fff', fontWeight: 700, whiteSpace: 'nowrap', borderRadius: '20px', px: 2, fontSize: 12, '&:hover': { background: 'linear-gradient(135deg, #FF5200 0%, #FF8C00 100%)', opacity: 0.9 } }}>Retry</Button> : null}
    >
        <Typography variant="body2" fontWeight={600} sx={{ color: C.text }}>
            {retrying ? 'Re-queued — fetching stats in the background…'
                : isFailed ? `Stats fetch failed for all ${total} posts. Click Retry.`
                : `Fetching — ${processedCount} / ${total} posts processed (${percentage}%)`}
        </Typography>
        {!retrying && !isFailed && (
            <LinearProgress variant="determinate" value={percentage}
                sx={{ mt: 1, height: 4, borderRadius: 2, bgcolor: 'rgba(0,0,0,0.08)', '& .MuiLinearProgress-bar': { background: 'linear-gradient(90deg, #FF5200 0%, #FF8C00 100%)' } }} />
        )}
    </Alert>
));
ProcessingBanner.displayName = 'ProcessingBanner';

// ── Network canvas banner (same as influencer discovery page) ─────────────────
const CampaignBanner = memo(({ campaignData, stats }) => {
    const canvasRef = useRef(null);
console.log("campaignData",campaignData)
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        let rafId = null, lastTs = 0, visible = true;
        const FPS_CAP = 1000 / 40;

        const resize = () => {
            canvas.width  = canvas.offsetWidth;
            canvas.height = canvas.offsetHeight;
        };
        resize();
        window.addEventListener('resize', resize, { passive: true });

        const io = new IntersectionObserver(([e]) => {
            visible = e.isIntersecting;
            if (visible && !rafId) rafId = requestAnimationFrame(animate);
        }, { threshold: 0 });
        io.observe(canvas);

        const nodes = Array.from({ length: 55 }, (_, i) => ({
            x: Math.random(), y: Math.random(),
            vx: (Math.random() - 0.5) * 0.00042,
            vy: (Math.random() - 0.5) * 0.00042,
            r:     i < 8 ? 9 + Math.random() * 5 : 3 + Math.random() * 2.5,
            isHub: i < 8,
            coral: Math.random() > 0.42,
            phase: Math.random() * Math.PI * 2,
        }));

        const animate = (ts) => {
            if (!visible) { rafId = null; return; }
            rafId = requestAnimationFrame(animate);
            if (ts - lastTs < FPS_CAP) return;
            lastTs = ts;

            const w = canvas.width, h = canvas.height;
            ctx.clearRect(0, 0, w, h);

            nodes.forEach(n => {
                n.x += n.vx; n.y += n.vy;
                if (n.x < 0) n.x = 1; if (n.x > 1) n.x = 0;
                if (n.y < 0) n.y = 1; if (n.y > 1) n.y = 0;
                if (n.isHub) n.phase += 0.048;
            });

            const linkPx = w * 0.17;
            for (let i = 0; i < nodes.length; i++) {
                for (let j = i + 1; j < nodes.length; j++) {
                    const dx = (nodes[i].x - nodes[j].x) * w;
                    const dy = (nodes[i].y - nodes[j].y) * h;
                    const dist = Math.sqrt(dx * dx + dy * dy);
                    if (dist < linkPx) {
                        const alpha = (1 - dist / linkPx) * 0.22;
                        ctx.beginPath();
                        ctx.moveTo(nodes[i].x * w, nodes[i].y * h);
                        ctx.lineTo(nodes[j].x * w, nodes[j].y * h);
                        ctx.strokeStyle = (nodes[i].coral || nodes[j].coral)
                            ? `rgba(255,120,60,${alpha})`
                            : `rgba(120,100,255,${alpha})`;
                        ctx.lineWidth = 1.4;
                        ctx.stroke();
                    }
                }
            }

            nodes.forEach(n => {
                const pulse = n.isHub ? Math.sin(n.phase) * 3 : 0;
                const r = Math.max(0.5, n.r + pulse);
                ctx.save();
                if (n.isHub) {
                    ctx.shadowColor = n.coral ? 'rgba(255,107,53,0.80)' : 'rgba(130,120,255,0.75)';
                    ctx.shadowBlur = 26;
                    ctx.beginPath();
                    ctx.arc(n.x * w, n.y * h, r, 0, Math.PI * 2);
                    ctx.fillStyle = n.coral ? 'rgba(255,107,53,0.94)' : 'rgba(140,130,255,0.90)';
                    ctx.fill();
                } else {
                    ctx.shadowColor = n.coral ? 'rgba(255,107,53,0.42)' : 'rgba(99,102,241,0.40)';
                    ctx.shadowBlur = 11;
                    ctx.beginPath();
                    ctx.arc(n.x * w, n.y * h, r, 0, Math.PI * 2);
                    ctx.fillStyle = n.coral ? 'rgba(255,107,53,0.74)' : 'rgba(110,100,240,0.70)';
                    ctx.fill();
                }
                ctx.restore();
            });
        };

        rafId = requestAnimationFrame(animate);
        return () => {
            cancelAnimationFrame(rafId);
            io.disconnect();
            window.removeEventListener('resize', resize);
        };
    }, []);

    const statItems = [
        { value: stats.total > 0 ? String(stats.total) : '—',                           label: 'Posts' },
        { value: stats.liveInfluencerCount > 0 ? String(stats.liveInfluencerCount) : '—', label: 'Influencers' },
        { value: stats.campaignViews !== '0' ?  stats.campaignViews : '—',                 label: 'Campaign Views' },
    ];

    return (
        <Box sx={{
            position: 'relative',
            mb: 2.5,
            height: '158px',
            borderRadius: '16px',
            background: 'linear-gradient(135deg, #0D1B3E 0%, #0A1228 60%, #111440 100%)',
            overflow: 'hidden',
            boxShadow: '0 4px 28px rgba(0,0,0,0.20), 0 1px 4px rgba(0,0,0,0.10)',
        }}>
            <canvas
                ref={canvasRef}
                style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', display: 'block', pointerEvents: 'none', zIndex: 0 }}
            />

            {/* Ambient glows */}
            <Box sx={{ position: 'absolute', width: 450, height: 450, top: '-130px', right: '14%', background: 'radial-gradient(circle, rgba(255,107,53,0.13) 0%, transparent 65%)', pointerEvents: 'none', zIndex: 0 }} />
            <Box sx={{ position: 'absolute', width: 340, height: 340, bottom: '-100px', left: '5%', background: 'radial-gradient(circle, rgba(99,102,241,0.11) 0%, transparent 65%)', pointerEvents: 'none', zIndex: 0 }} />

            {/* Content */}
            <Box sx={{ position: 'relative', zIndex: 1, height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: { xs: 2.5, md: 4 } }}>
                <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                        <Box component="span" sx={{ display: 'inline-block', color: '#FF6B35', bgcolor: 'rgba(255,107,53,0.12)', border: '1px solid rgba(255,107,53,0.28)', px: '10px', py: '3px', borderRadius: '100px', fontSize: '9px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                            Campaign Report
                        </Box>
                        {campaignData.campaignName && (
                            <Box component="span" sx={{ display: 'inline-block', color: 'rgba(255,255,255,0.75)', bgcolor: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.15)', px: '10px', py: '3px', borderRadius: '100px', fontSize: '9px', fontWeight: 600, letterSpacing: '0.06em', backdropFilter: 'blur(4px)' }}>
                                {campaignData.campaignName}
                            </Box>
                        )}
                    </Box>
                    <Typography sx={{ fontSize: { xs: '17px', md: '22px' }, fontWeight: 800, color: 'white', lineHeight: 1.2 }}>
                        {campaignData.displayName}
                    </Typography>
                    <Typography sx={{ fontSize: '12.5px', color: 'rgba(255,255,255,0.40)', mt: 0.6 }}>
                        {campaignData.platform}&nbsp;&nbsp;·&nbsp;&nbsp;Analytics Dashboard
                    </Typography>
                </Box>

                <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 1.5 }}>
                    {statItems.map(({ value, label }) => (
                        <Box key={label} sx={{ px: 2.5, py: 1.5, borderRadius: '12px', bgcolor: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.10)', backdropFilter: 'blur(8px)', textAlign: 'center' }}>
                            <Typography sx={{ fontSize: '20px', fontWeight: 800, color: 'white', lineHeight: 1 }}>{value}</Typography>
                            <Typography sx={{ fontSize: '9px', color: 'rgba(255,255,255,0.35)', mt: 0.6, textTransform: 'uppercase', letterSpacing: '0.07em' }}>{label}</Typography>
                        </Box>
                    ))}
                </Box>
            </Box>
        </Box>
    );
});
CampaignBanner.displayName = 'CampaignBanner';

// ── Tab button ────────────────────────────────────────────────────────────────
const TabBtn = memo(({ id, icon: Icon, label, isActive, onClick }) => (
    <Box
        onClick={() => onClick(id)}
        component={motion.div}
        whileTap={{ scale: 0.95 }}
        sx={{
            display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 0.75 },
            px: { xs: 1.5, sm: 2.5 }, py: { xs: 0.85, sm: 1.1 },
            borderRadius: 1.75, cursor: 'pointer',
            transition: 'all 0.18s ease',
            background: isActive ? 'linear-gradient(135deg, #FF5200 0%, #FF8C00 100%)' : 'transparent',
            boxShadow: isActive ? '0 4px 18px rgba(255,82,0,0.35)' : 'none',
            '&:hover': { background: isActive ? 'linear-gradient(135deg, #FF5200 0%, #FF8C00 100%)' : 'rgba(0,0,0,0.05)' },
        }}
    >
        <Icon sx={{ fontSize: { xs: 14, sm: 15 }, color: isActive ? '#fff' : C.textSub, transition: 'color 0.18s' }} />
        <Typography sx={{ fontSize: { xs: 12, sm: 13 }, fontWeight: 700, color: isActive ? '#fff' : C.textSub, transition: 'color 0.18s', whiteSpace: 'nowrap' }}>
            {label}
        </Typography>
    </Box>
));
TabBtn.displayName = 'TabBtn';

// ── Download menu option — icon-chip + title/caption row, industry-standard
// file-type colors (Excel green, PDF red) so the two formats read at a glance ──
const DownloadMenuOption = memo(({ icon: Icon, color, title, caption, onClick }) => (
    <MenuItem
        onClick={onClick}
        sx={{
            borderRadius: '10px', mx: 0.5, my: 0.25, px: 1.25, py: 1,
            display: 'flex', alignItems: 'center', gap: 1.25,
            '&:hover': { bgcolor: `${color}14` },
        }}
    >
        <Box sx={{
            width: 36, height: 36, borderRadius: '10px', flexShrink: 0,
            bgcolor: `${color}18`, display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
            <Icon sx={{ fontSize: 19, color }} />
        </Box>
        <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: C.text, lineHeight: 1.3 }}>
                {title}
            </Typography>
            <Typography sx={{ fontSize: 11, color: C.textMuted, lineHeight: 1.3, whiteSpace: 'nowrap' }}>
                {caption}
            </Typography>
        </Box>
    </MenuItem>
));
DownloadMenuOption.displayName = 'DownloadMenuOption';

// ── Tab config ────────────────────────────────────────────────────────────────
const TAB_CONFIG = [
    {
        id: 0, icon: PieChartOutlineOutlinedIcon, label: 'Dashboard',
        getProps: (report, stats, snapshots, nav) => ({ report, stats, snapshots, onNavigateTab: nav }),
        Component: DashboardTab,
    },
    {
        id: 1, icon: PersonSearchOutlinedIcon, label: 'Influencers',
        getProps: (report) => {
            // Grouped by creator only — NOT gated on apiHit. "Update Now" flips every
            // post to apiHit:false while it re-fetches; gating on it made the Shares
            // input unmount mid-edit during a refresh, silently dropping the save.
            const postsByCreator = {};
            for (const p of report?.posts ?? []) {
                const u = p.creator?.username;
                if (u) (postsByCreator[u] = postsByCreator[u] || []).push(p);
            }
            return {
                reportId: report?._id,
                platform: report?.platform?.name ?? null,
                influencers: report?.influencers?.length
                    ? report.influencers.map(inf => mapInfluencerForTable(inf, {
                        totalPayment: Number(report?.totalPayment) || 0,
                        influencerCount: report.influencers.length,
                        creatorPosts: postsByCreator[inf.username] ?? [],
                      }))
                    : undefined,
            };
        },
        Component: InfluencersTab,
    },
    {
        id: 2, icon: CollectionsOutlinedIcon, label: 'Contents',
        getProps: (report) => ({ report }),
        Component: ContentsTab,
    },
];

// ── Main component ────────────────────────────────────────────────────────────
// `reportIdOverride` / `embedded` let ReportGroupDashboard.jsx reuse this whole
// component for a single child report inside a group's tab bar, without
// duplicating the app header/sidebar chrome it renders when visited directly
// at /reports/:id/dashboard. Both are optional and default to the original
// standalone behavior — existing usage (App.js route, no props) is unchanged.
const CampaignReportDashboard = ({ reportIdOverride, embedded = false } = {}) => {
    const { id: idFromParams } = useParams();
    const id = reportIdOverride || idFromParams;
    console.log("id",)
    const { state, search } = useLocation();
    const navigate   = useNavigate();
    const [tab, setTab] = useState(0);
    const [retrying, setRetrying] = useState(false);
    const [isManualRefreshing, setIsManualRefreshing] = useState(false);
    const [addPostsOpen, setAddPostsOpen] = useState(false);
    const [commentsRequested, setCommentsRequested] = useState(false);
    const [campaignPickerOpen, setCampaignPickerOpen] = useState(false);
    const [attaching, setAttaching] = useState(false);
    const [attachToast, setAttachToast] = useState(null); // { severity, message } — errors only
    const [downloadingExcel, setDownloadingExcel] = useState(false);
    const [downloadingPdf, setDownloadingPdf] = useState(false);
    const [downloadMenuAnchor, setDownloadMenuAnchor] = useState(null);
    const [downloadToast, setDownloadToast] = useState(null); // { severity, message } — errors only

    // ?print=1 drives the backend's headless-Chrome PDF export (routes/report.js's
    // /:id/export-pdf). Strips interactive chrome, renders all 3 tabs stacked with a
    // page-break between each, and sets a [data-pdf-ready] marker once settled —
    // that's Puppeteer's cue to print. Chromium paginates naturally at a fixed page
    // size from there, so a long influencer/post list just flows onto extra pages.
    const isPrintMode = useMemo(() => new URLSearchParams(search).get('print') === '1', [search]);
    const [printReady, setPrintReady] = useState(false);
    const [attachResult, setAttachResult] = useState(null); // { campaignId, campaignName, result } — success summary modal
    const theme    = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const queryClient = useQueryClient();
    const { data: response, isLoading, isError, processingStatus } = useFetchReportWithPolling(id);
    const { data: postCommentsResponse } = useQuery({
        queryKey: ['postComments', id],
        queryFn:  () => reportServices.GetPostComments(id),
        enabled:  commentsRequested && !!id,
        staleTime: 5 * 60 * 1000,
    });
    const { data: snapshotsResponse }             = useFetchReportSnapshots(id);
    const { data: refreshInfoResponse }           = useFetchRefreshInfo(id, { enabled: !isPrintMode });
    const { data: statusResponse }                = useFetchReportStatus(id, isManualRefreshing);
    const report    = response?.data ?? state?.report ?? null;
    const snapshots = snapshotsResponse?.data ?? [];

    const backfilledRef = useRef(false);
    useEffect(() => {
        if (backfilledRef.current || !report || !id) return;
        const influencers = report.influencers || [];
        const allDone    = report.posts?.length > 0 && report.posts.every(p => p.apiHit);
        const needsFill  = allDone && influencers.length > 0 && influencers.some(inf => !inf.followerCount);
        if (!needsFill) return;
        backfilledRef.current = true;
        reportServices.BackfillFollowers(id)
            .then(() => { queryClient.resetQueries({ queryKey: ['reportDetails', id] }); })
            .catch(() => { backfilledRef.current = false; });
    }, [id, report, queryClient]);

    const isPublicView  = useMemo(() => checkIsPublicView(), []);
    const stats         = useMemo(() => computeStats(report), [report]);
    const handleTabChange = useCallback((i) => setTab(i), []);
    const handleRetry     = useCallback(async () => {
        setRetrying(true);
        try { await reportServices.RetryReport(id); } catch (_) {}
    }, [id]);

    // Detect manual refresh completion and refresh all data
    useEffect(() => {
        if (!isManualRefreshing || !statusResponse?.data?.isComplete) return;
        setIsManualRefreshing(false);
        queryClient.invalidateQueries({ queryKey: ['reportDetails', id] });
        queryClient.invalidateQueries({ queryKey: ['reportRefreshInfo', id] });
        queryClient.invalidateQueries({ queryKey: ['reportSnapshots', id] });
    }, [statusResponse?.data?.isComplete, isManualRefreshing, id, queryClient]);

    const handleUpdateNow = useCallback(async () => {
        setIsManualRefreshing(true);
        try {
            await reportServices.UpdateNow(id);
        } catch (err) {
            setIsManualRefreshing(false);
            // 429 means backend rejected due to stale frontend cooldown state.
            // Refetch refresh-info so lastManualRefreshAt syncs and the button
            // correctly shows as disabled without requiring a page reload.
            if (err?.response?.status === 429) {
                queryClient.invalidateQueries({ queryKey: ['reportRefreshInfo', id] });
            }
        }
    }, [id, queryClient]);

    const handleDownloadExcel = useCallback(async () => {
        setDownloadingExcel(true);
        try {
            const { data } = await reportServices.DownloadReportExcel(id);
            if (data?.downloadUrl) {
                triggerFileDownload(data.downloadUrl, data.filename);
            } else {
                setDownloadToast({ severity: 'error', message: "Couldn't generate the Excel report — try again." });
            }
        } catch (err) {
            setDownloadToast({ severity: 'error', message: err?.response?.data?.message || "Couldn't download the report — try again." });
        } finally {
            setDownloadingExcel(false);
        }
    }, [id]);

    // Server-side PDF export — headless Chrome (Puppeteer, already a backend
    // dependency) navigates to this same page with ?print=1 and prints it natively.
    // Replaces an earlier client-side html2canvas attempt that broke two different
    // ways (tainted canvas on cross-origin images, then a gradient/canvas-sizing
    // bug) — both are classes of bug that don't exist with real browser rendering.
    // Same request/response shape as handleDownloadExcel above.
    const handleDownloadPdf = useCallback(async () => {
        setDownloadingPdf(true);
        try {
            const { data } = await reportServices.DownloadReportPdf(id);
            if (data?.downloadUrl) {
                triggerFileDownload(data.downloadUrl, data.filename);
            } else {
                setDownloadToast({ severity: 'error', message: "Couldn't generate the PDF — try again." });
            }
        } catch (err) {
            setDownloadToast({ severity: 'error', message: err?.response?.data?.message || "Couldn't generate the PDF — try again." });
        } finally {
            setDownloadingPdf(false);
        }
    }, [id]);

    // Chromium's print-to-PDF embeds <img> content at its SOURCE resolution, not its
    // on-screen display size — a page with 30 untouched Instagram thumbnails (each
    // 1080px+) took ~14s to render and produced a 46MB file that then took another
    // ~14s just to upload. Route every image through the backend's resizing proxy
    // (?w=500) before printing — cuts both dramatically.
    const shrinkImagesForPrint = useCallback(async () => {
        const proxyBase = `${process.env.REACT_APP_API_URL}report/proxy-image?w=500&url=`;
        const imgs = Array.from(document.querySelectorAll('img'));
        await Promise.all(imgs.map((img) => {
            const src = img.getAttribute('src') || '';
            if (!src || src.includes('/report/proxy-image')) return null;
            return new Promise((resolve) => {
                img.onload = resolve;
                img.onerror = resolve; // one broken image shouldn't hold up the whole export
                img.src = `${proxyBase}${encodeURIComponent(src)}`;
            });
        }));
    }, []);

    // All 3 tabs render stacked in one pass (see the isPrintMode JSX branch below) —
    // no per-tab page sizing, no tab-switching. Chromium paginates naturally at a
    // fixed page size (set server-side in routes/report.js), honoring the CSS
    // break-before:page between sections; a long influencer/post list simply flows
    // onto as many extra pages as it needs, rather than being forced onto one giant
    // custom-height page or truncated to fit a fixed page count.
    useEffect(() => {
        if (!isPrintMode || isLoading || !report) return;
        setPrintReady(false);
        let cancelled = false;
        const run = async () => {
            // Settle window: clears recharts' ~1.5s default chart animation and
            // ContentsTab's count-up numbers, which all mount simultaneously here.
            await new Promise((r) => setTimeout(r, 1800));
            if (cancelled) return;
            await shrinkImagesForPrint();
            if (!cancelled) setPrintReady(true);
        };
        run();
        return () => { cancelled = true; };
    }, [isPrintMode, isLoading, report, shrinkImagesForPrint]);

    const handleDeletePost = useCallback(async (link) => {
        try {
            await reportServices.RemovePostFromReport({ reportId: id, link });
            queryClient.invalidateQueries({ queryKey: ['reportDetails', id] });
        } catch (_) {}
    }, [id, queryClient]);

    const handleStopUpdates = useCallback(async () => {
        try {
            await reportServices.StopUpdates(id);
            queryClient.invalidateQueries({ queryKey: ['reportRefreshInfo', id] });
        } catch (_) {}
    }, [id, queryClient]);

    const handleResumeUpdates = useCallback(async () => {
        try {
            await reportServices.ResumeUpdates(id);
            queryClient.invalidateQueries({ queryKey: ['reportRefreshInfo', id] });
        } catch (_) {}
    }, [id, queryClient]);

    // Attaches this report's current influencers/posts to an existing campaign's
    // payment queue. Safe to re-run any time — content already queued for a
    // creator (by post) is left alone, only genuinely new content is added.
    // Results (what was newly added vs. already present, per creator) are
    // shown in a dedicated summary modal rather than a toast, and navigation
    // to the Payment Queue is a deliberate action from that modal — not
    // automatic — so the user actually sees what happened before moving on.
    const handleAttachToCampaign = useCallback(async (campaign) => {
        setCampaignPickerOpen(false);
        setAttaching(true);
        try {
            const result = await paymentportalservice.AttachReportToCampaign(id, campaign._id);
            queryClient.invalidateQueries({ queryKey: ['reportDetails', id] });
            setAttachResult({ campaignId: campaign._id, campaignName: campaign.name, result });
        } catch (err) {
            setAttachToast({
                severity: 'error',
                message: err?.response?.data?.message || 'Failed to attach report to campaign. Please try again.',
            });
        } finally {
            setAttaching(false);
        }
    }, [id, queryClient]);

    const postCommentDocs = postCommentsResponse?.data ?? null;
    const handleCommentsLoaded = useCallback(() => setCommentsRequested(true), []);

    const handleBudgetSave = useCallback(async (amount) => {
        await reportServices.UpdateBudget(id, amount);
        queryClient.invalidateQueries({ queryKey: ['reportDetails', id] });
    }, [id, queryClient]);

    const handleInfluencerBudgetSave = useCallback(async (username, budget) => {
        await reportServices.UpdateInfluencerBudget(id, username, budget);
        queryClient.invalidateQueries({ queryKey: ['reportDetails', id] });
    }, [id, queryClient]);

    const handleInfluencerZoneSave = useCallback(async (username, zone) => {
        await reportServices.UpdateInfluencerZone(id, username, zone);
        queryClient.invalidateQueries({ queryKey: ['reportDetails', id] });
    }, [id, queryClient]);

    const handlePostSharesSave = useCallback(async (link, shares) => {
        try {
            await reportServices.UpdatePostShares(id, link, shares);
        } catch (err) {
            // The inline cell swallows errors and just reverts — surface why here.
            // Reuses the generic error toast rather than adding a third Snackbar.
            const data = err?.response?.data;
            setAttachToast({ severity: 'error', message: `Couldn't save shares: ${typeof data === 'string' && data ? data : (err?.message || 'please try again.')}` });
            throw err;
        }
        queryClient.invalidateQueries({ queryKey: ['reportDetails', id] });
    }, [id, queryClient]);

    const handleInfluencerRetailerCodeSave = useCallback(async (username, retailerCode) => {
        await reportServices.UpdateInfluencerRetailerCode(id, username, retailerCode);
        queryClient.invalidateQueries({ queryKey: ['reportDetails', id] });
    }, [id, queryClient]);

    const enrichedReport = useMemo(() => {
        if (!report) return null;
        const allInfluencers = (report.influencers || [])
            .map(inf => ({
                id: inf.username, handle: '@' + inf.username, name: inf.fullName || inf.username,
                followers: inf.followerCount || 0, posts: inf.postCount || 0,
                engagements: (inf.totalLikes || 0) + (inf.totalComments || 0) + (inf.totalReshares || 0),
                reelViews: inf.totalViews || 0, avatar: proxyImgUrl(inf.profilePicUrl), category: '',
            }))
            .sort((a, b) => b.engagements - a.engagements);
        const topInfluencers = allInfluencers.slice(0, 12);
        const topContents = (report.posts || [])
            .filter(p => p.apiHit)
            .map(p => {
                const caption = extractCaption(p.caption);
                return {
                    id: p.postId || p.shortcode || p.link, handle: p.creator?.username ? '@' + p.creator.username : '',
                    username: p.creator?.fullName || p.creator?.username || '', caption,
                    likes: p.like_count || 0, comments: p.comment_count || 0, views: p.views || 0,
                    totalPlays: p.totalPlays || p.views || 0, fbPlays: p.fbPlays || 0,
                    saves: p.save_count || 0, reshares: p.reshare_count || 0,
                    date: p.publishedAt ? new Date(p.publishedAt * 1000).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: '2-digit', timeZone: 'Asia/Kolkata' }) : '',
                    thumbnailUrl: p.thumbnailUrl || null, gradient: stringToGradient(p.creator?.username || p.link || ''),
                    overlayText: caption.slice(0, 80) || null, overlayBg: '#333', overlayColor: '#fff',
                    isPaidPartnership: p.isPaidPartnership || false, likesHidden: p.likesHidden || false,
                    videoDuration: p.videoDuration || null, hashtags: p.hashtags || [],
                    contentLang: p.contentLang || null, link: p.link,
                };
            })
            .sort((a, b) => b.likes - a.likes).slice(0, 12);
        const hashtagCounts = {};
        for (const p of report.posts || []) {
            for (const tag of (p.hashtags || [])) {
                const t = (tag || '').toLowerCase().replace(/^#/, '');
                if (t) hashtagCounts[t] = (hashtagCounts[t] || 0) + 1;
            }
        }
        const topHashtags = Object.entries(hashtagCounts).sort((a, b) => b[1] - a[1]).slice(0, 20).map(([tag, c]) => ['#' + tag, c]);
        const STOPWORDS = new Set(['the','a','an','and','or','but','in','on','at','to','for','of','with','is','it','this','that','was','are','be','as','by','from','we','our','your','i','you','my','me','us']);
        const wordCounts = {};
        for (const p of report.posts || []) {
            const words = extractCaption(p.caption).toLowerCase().match(/[a-z]{4,}/g) || [];
            for (const w of words) if (!STOPWORDS.has(w)) wordCounts[w] = (wordCounts[w] || 0) + 1;
        }
        const topWords = Object.entries(wordCounts).sort((a, b) => b[1] - a[1]).slice(0, 20).map(([word, c]) => [word, c]);

        // ── Keywords (top 10 capitalized words for CommentInsights) ──────────────
        const keywords = topWords.slice(0, 10).map(([w, c]) => [w.charAt(0).toUpperCase() + w.slice(1), c]);

        // ── Determine text source: real comments if fetched, else captions ──────
        // Comments now live in PostComment collection (not embedded) — sourced via
        // postCommentDocs once the user clicks "Load Comments".
        const commentsByLink = new Map((postCommentDocs || []).map(d => [d.postLink, d.comments]));
        const allComments = (report.posts || [])
            .filter(p => p.apiHit)
            .flatMap(p => commentsByLink.get(p.link) || []);
        const commentsLoaded   = allComments.length > 0;
        const insightTexts     = commentsLoaded
            ? allComments
            : (report.posts || []).filter(p => p.apiHit).map(p => extractCaption(p.caption));

        // ── Emojis ───────────────────────────────────────────────────────────────
        const EMOJI_NAMES = {
            // Hearts & love
            '❤️':'Love','🧡':'Orange Heart','💛':'Yellow Heart','💚':'Green Heart',
            '💙':'Blue Heart','💜':'Purple Heart','🖤':'Black Heart','🤍':'White Heart',
            '💕':'Hearts','💞':'Revolving Hearts','💓':'Beating Heart','💗':'Growing Heart',
            '💝':'Heart with Ribbon','🫶':'Heart Hands','❤‍🔥':'Heart on Fire',
            // Happy & positive
            '😂':'Joy','🤣':'ROFL','😊':'Smile','😍':'Heart Eyes','🥰':'Smiling Hearts',
            '😘':'Kiss','😁':'Grin','😆':'Laughing','🥹':'Touched','😄':'Smile Big',
            '🤩':'Starstruck','😎':'Cool','🥳':'Partying','😏':'Smirk',
            // Sad & crying
            '😭':'Crying','😢':'Sad','🥺':'Pleading','😔':'Pensive','😞':'Disappointed',
            '😩':'Weary','😫':'Tired',
            // Surprised & shocked
            '😮':'Surprised','😲':'Astonished','🤯':'Mind Blown','😱':'Screaming',
            // Other faces
            '🙃':'Upside Down','😅':'Nervous','😬':'Grimace','🤔':'Thinking',
            '😴':'Sleeping','🤗':'Hug Face','😤':'Huffing',
            // Hands & gestures
            '👏':'Clapping','🙌':'Raising Hands','👍':'Thumbs Up','👎':'Thumbs Down',
            '🙏':'Pray','🤝':'Handshake','💪':'Strong','✌️':'Peace','🤞':'Fingers Crossed',
            '👌':'OK','🫂':'Hug',
            // Nature & misc
            '🔥':'Fire','✨':'Sparkles','⭐':'Star','🌟':'Glowing Star','💫':'Dizzy',
            '🎉':'Celebration','🎊':'Party','🎯':'Bullseye','🚀':'Rocket','💎':'Diamond',
            '👑':'Crown','🌸':'Cherry Blossom','🦋':'Butterfly','🌙':'Moon',
            '☕':'Coffee','🍕':'Pizza','💯':'Perfect','💥':'Boom','🎵':'Music',
            '🎶':'Musical Notes','🤑':'Money Face','😜':'Winking Tongue',
            '💀':'Skull','👻':'Ghost','🤖':'Robot',
        };

        // Use Intl.Segmenter (grapheme clusters) so ❤️ stays ❤️ not ❤
        const emojiCounts = {};
        const hasSegmenter = typeof Intl !== 'undefined' && Intl.Segmenter;
        for (const text of insightTexts) {
            let chars = [];
            if (hasSegmenter) {
                const seg = new Intl.Segmenter(undefined, { granularity: 'grapheme' });
                chars = [...seg.segment(text || '')].map(s => s.segment).filter(s => /\p{Extended_Pictographic}/u.test(s));
            } else {
                // Fallback: capture emoji + variation selector + optional ZWJ sequences
                chars = (text || '').match(/\p{Extended_Pictographic}[️⃣]?(?:‍\p{Extended_Pictographic}[️⃣]?)*/gu) || [];
            }
            for (const e of chars) {
                // Only strip skin tone modifiers — preserve variation selectors so ❤️ stays ❤️
                const base = e.replace(/[\u{1F3FB}-\u{1F3FF}]/gu, '');
                if (base) emojiCounts[base] = (emojiCounts[base] || 0) + 1;
            }
        }
        const topEmojis = Object.entries(emojiCounts)
            .sort((a, b) => b[1] - a[1])
            .slice(0, 10)
            .map(([emoji, count]) => [emoji, EMOJI_NAMES[emoji] || '', count]);

        // ── Sentiment from captions (positive/negative word scoring) ─────────────
        const POS = new Set(['great','amazing','love','awesome','excellent','good','wonderful','beautiful','fantastic','best','incredible','perfect','happy','joy','excited','superb','brilliant','outstanding','magnificent','delightful','glad','lovely','stunning','gorgeous','impressive','inspiring','iconic','legendary','phenomenal','epic','cute','proud','blessed','grateful','positive','vibrant','memorable','classic','wow','fun','clean','fresh','smooth']);
        const NEG = new Set(['bad','terrible','awful','horrible','worst','hate','disappointing','poor','sad','angry','frustrating','boring','dull','mediocre','unacceptable','disgusting','offensive','pathetic','ugly','fail','waste','wrong','dirty','cheap','broken','slow','mess','late','noisy','rude','overpriced']);
        let posCount = 0, negCount = 0;
        for (const text of insightTexts) {
            const words = (text || '').toLowerCase().match(/[a-z]{3,}/g) || [];
            for (const w of words) {
                if (POS.has(w)) posCount++;
                else if (NEG.has(w)) negCount++;
            }
        }
        const sentimentTotal = posCount + negCount;
        const positivePct = sentimentTotal > 0 ? parseFloat(((posCount / sentimentTotal) * 100).toFixed(2)) : null;
        const negativePct = positivePct != null ? parseFloat((100 - positivePct).toFixed(2)) : null;
        const sentiment = positivePct != null ? [
            { name: 'Positive Sentiment', value: positivePct, color: '#8C7CF0' },
            { name: 'Negative Sentiment', value: negativePct, color: '#E8E6FF' },
        ] : null;

        return { ...report, allInfluencers, topInfluencers, topContents, topHashtags, topWords, keywords, topEmojis, sentiment, commentsLoaded };
    }, [report, postCommentDocs]);

    const campaignData = useMemo(() => {
        const name         = report?.name ?? '—';
        const platform     = report?.platform?.name ?? '—';
        const campaignName = report?.campaign?.name ?? null;
        const displayName  = isMobile && name.length > 30 ? name.slice(0, 30) + '…' : name;
        return { name, platform, displayName, campaignName };
    }, [report, isMobile]);

    // Layout helpers — public view removes the app sidebar (80px) and uses a slim fixed header (56px).
    // Embedded (nested inside ReportGroupDashboard) skips both the header chrome and the
    // sidebar offset entirely, since the parent page already provides its own. Print mode
    // (Puppeteer's ?print=1) needs the exact same chrome-free layout as embedded.
    const effectiveEmbedded = embedded || isPrintMode;
    const contentWidth  = effectiveEmbedded ? '100%' : (isPublicView ? '100%' : { xs: '100%', lg: 'calc(100% - 80px)' });
    const contentLeft   = effectiveEmbedded ? 0      : (isPublicView ? 0      : { xs: '0px', lg: '80px' });
    const contentTop    = effectiveEmbedded ? 0      : (isPublicView ? '56px' : 8); // MUI spacing 8 = 64px

    if (isLoading && !report) {
        return (
            <>
                {!effectiveEmbedded && (isPublicView ? <PublicReportHeader /> : <DashboardHeaderSection hideSearch />)}
                <Box width={contentWidth} marginLeft={contentLeft} marginTop={contentTop}
                    sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh', background: 'linear-gradient(145deg, #EDF1FF 0%, #F2EEFF 38%, #FFF0ED 100%)' }}>
                    <CircularProgress size={48} thickness={4} sx={{ color: C.orange }} />
                </Box>
            </>
        );
    }
    if (isError && !report) {
        return (
            <>
                {!effectiveEmbedded && (isPublicView ? <PublicReportHeader /> : <DashboardHeaderSection hideSearch />)}
                <Box width={contentWidth} marginLeft={contentLeft} marginTop={contentTop} sx={{ p: 3, background: 'linear-gradient(145deg, #EDF1FF 0%, #F2EEFF 38%, #FFF0ED 100%)' }}>
                    <Alert severity="error">Failed to load report. Please try again.</Alert>
                </Box>
            </>
        );
    }

    return (
        <>
            {!effectiveEmbedded && (isPublicView ? <PublicReportHeader campaignName={campaignData.name} /> : <DashboardHeaderSection hideSearch />)}
            <Box
                component={motion.div}
                variants={pageIn}
                initial="hidden"
                animate="show"
                width={contentWidth}
                marginLeft={contentLeft}
                marginTop={contentTop}
                sx={{
                    background: 'linear-gradient(145deg, #EDF1FF 0%, #F2EEFF 38%, #FFF0ED 100%)',
                    position: 'relative',
                    minHeight: '100vh', p: { xs: 1.5, sm: 2, md: 3 },
                    overflowX: 'clip',      // clip without creating a scroll container — preserves position:sticky
                    boxSizing: 'border-box',
                }}
            >
                {/* Dot-grid overlay — same as influencer discovery page. Skipped in print
                    mode: it's a repeating pattern tiled across the whole page, and once
                    stacked print content spans several page-heights, Chromium's print
                    engine rasterizes that many repetitions instead of using a native PDF
                    tile fill — purely decorative on screen, but it was most of a report's
                    45MB PDF (real photos, already downscaled, accounted for barely 1MB). */}
                {!isPrintMode && (
                    <Box sx={{ position: 'absolute', inset: 0, backgroundImage: 'radial-gradient(rgba(99,102,241,0.06) 1.5px, transparent 1.5px)', backgroundSize: '28px 28px', pointerEvents: 'none', zIndex: 0 }} />
                )}

                {/* All content above the dot grid */}
                <Box sx={{ position: 'relative', zIndex: 1, width: '100%', minWidth: 0, overflowX: 'clip' }}>
                <CampaignBanner campaignData={campaignData} stats={stats} />

                {!isPrintMode && (stats.isProcessing || stats.isStatsFailed) && (
                    <ProcessingBanner
                        processedCount={processingStatus?.processed ?? stats.processedCount}
                        total={processingStatus?.total ?? stats.total}
                        percentage={processingStatus?.percentage ?? stats.percentage}
                        onRetry={handleRetry}
                        retrying={retrying}
                        isFailed={stats.isStatsFailed && !stats.isProcessing && (!processingStatus || processingStatus.isComplete)}
                        isPublic={isPublicView}
                    />
                )}

                {!isPrintMode && (
                    <RefreshInfoBar
                        lastUpdatedAt={refreshInfoResponse?.data?.lastUpdatedAt}
                        lastManualRefreshAt={refreshInfoResponse?.data?.lastManualRefreshAt}
                        nextRefreshAt={refreshInfoResponse?.data?.autoRefreshStopped ? null : refreshInfoResponse?.data?.nextRefreshAt}
                        isRefreshing={refreshInfoResponse?.data?.isRefreshing ?? false}
                        autoRefreshStopped={refreshInfoResponse?.data?.autoRefreshStopped ?? false}
                        onUpdateNow={handleUpdateNow}
                        onStopUpdates={handleStopUpdates}
                        onResumeUpdates={handleResumeUpdates}
                        isManualRefreshing={isManualRefreshing}
                        refreshProgress={statusResponse?.data ?? null}
                        isPublic={isPublicView}
                    />
                )}

                {/* Download Report is always visible (internal + public share view);
                    Add Posts / Attach to Campaign stay internal-only management actions.
                    All of it is interactive-only chrome, hidden entirely in ?print=1 mode. */}
                {!isPrintMode && (
                <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Button
                        size="small"
                        startIcon={(downloadingExcel || downloadingPdf) ? <CircularProgress size={12} thickness={5} sx={{ color: 'inherit' }} /> : <FileDownloadOutlinedIcon sx={{ fontSize: 14 }} />}
                        endIcon={(downloadingExcel || downloadingPdf) ? null : <ArrowDropDownIcon sx={{ fontSize: 18 }} />}
                        disabled={downloadingExcel || downloadingPdf}
                        onClick={(e) => setDownloadMenuAnchor(e.currentTarget)}
                        sx={{
                            fontSize: 11.5, fontWeight: 700, px: 1.75, py: 0.5,
                            borderRadius: '20px', textTransform: 'none', lineHeight: 1.4,
                            color: '#fff', background: 'linear-gradient(135deg, #FF5200 0%, #FF8C00 100%)',
                            '&:hover': { background: 'linear-gradient(135deg, #FF5200 0%, #FF8C00 100%)', opacity: 0.92 },
                            '&.Mui-disabled': { color: 'rgba(255,255,255,0.7)', background: 'linear-gradient(135deg, #FF5200 0%, #FF8C00 100%)', opacity: 0.6 },
                        }}
                    >
                        {downloadingExcel ? 'Preparing Excel…' : downloadingPdf ? 'Preparing PDF…' : 'Download Report'}
                    </Button>
                    <Menu
                        anchorEl={downloadMenuAnchor}
                        open={!!downloadMenuAnchor}
                        onClose={() => setDownloadMenuAnchor(null)}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                        PaperProps={{
                            elevation: 0,
                            sx: {
                                mt: 1, minWidth: 270, borderRadius: '16px', p: 0.75,
                                border: `1px solid ${C.border}`,
                                boxShadow: '0 16px 40px rgba(11,11,11,0.14), 0 4px 14px rgba(11,11,11,0.06)',
                            },
                        }}
                        MenuListProps={{ sx: { py: 0.5 } }}
                    >
                        <Typography sx={{
                            fontSize: 10.5, fontWeight: 700, color: C.textMuted,
                            textTransform: 'uppercase', letterSpacing: '0.08em',
                            px: 1.5, pt: 0.5, pb: 0.75,
                        }}>
                            Export Report
                        </Typography>
                        <DownloadMenuOption
                            icon={TableChartOutlinedIcon}
                            color="#1D6F42"
                            title="Excel"
                            caption="Full data — every influencer & post"
                            onClick={() => { setDownloadMenuAnchor(null); handleDownloadExcel(); }}
                        />
                        <DownloadMenuOption
                            icon={PictureAsPdfOutlinedIcon}
                            color="#E5252A"
                            title="PDF"
                            caption="Dashboard, Influencers & Contents"
                            onClick={() => { setDownloadMenuAnchor(null); handleDownloadPdf(); }}
                        />
                    </Menu>

                    {!isPublicView && (
                        <>
                            {!report?.campaign && (
                                <Button
                                    size="small"
                                    startIcon={<AddLinkIcon sx={{ fontSize: 14 }} />}
                                    onClick={() => setAddPostsOpen(true)}
                                    sx={{
                                        fontSize: 11.5, fontWeight: 600, px: 1.5, py: 0.5,
                                        borderRadius: '20px', textTransform: 'none', lineHeight: 1.4,
                                        color: '#FF5200', border: '1px solid rgba(255,82,0,0.35)',
                                        bgcolor: 'transparent',
                                        '&:hover': { bgcolor: 'rgba(255,82,0,0.06)', borderColor: '#FF5200' },
                                    }}
                                >
                                    Add Posts
                                </Button>
                            )}

                            <Button
                                size="small"
                                startIcon={attaching ? <CircularProgress size={12} thickness={5} sx={{ color: 'inherit' }} /> : <CampaignOutlinedIcon sx={{ fontSize: 14 }} />}
                                disabled={attaching}
                                onClick={() => setCampaignPickerOpen(true)}
                                sx={{
                                    fontSize: 11.5, fontWeight: 600, px: 1.5, py: 0.5,
                                    borderRadius: '20px', textTransform: 'none', lineHeight: 1.4,
                                    color: '#7C6FF7', border: '1px solid rgba(124,111,247,0.35)',
                                    bgcolor: 'transparent',
                                    '&:hover': { bgcolor: 'rgba(124,111,247,0.06)', borderColor: '#7C6FF7' },
                                }}
                            >
                                Attach to Campaign
                            </Button>

                            {report?.attachedCampaigns?.length > 0 && (
                                <>
                                    <Typography sx={{ fontSize: 11, color: '#9CA3AF', ml: 0.25 }}>
                                        Attached to:
                                    </Typography>
                                    {report.attachedCampaigns.map((c) => (
                                        <Chip
                                            key={c.campaignId}
                                            size="small"
                                            clickable
                                            label={c.name || 'Untitled campaign'}
                                            onClick={() => navigate(`/payments/campaigns/details/${c.campaignId}`)}
                                            sx={{
                                                fontSize: 11, fontWeight: 600, color: '#7C6FF7',
                                                bgcolor: 'rgba(124,111,247,0.08)', border: '1px solid rgba(124,111,247,0.2)',
                                                '&:hover': { bgcolor: 'rgba(124,111,247,0.14)' },
                                            }}
                                        />
                                    ))}
                                </>
                            )}
                        </>
                    )}
                </Box>
                )}

                <AddManualLinksModal
                    open={addPostsOpen}
                    onClose={() => setAddPostsOpen(false)}
                    reportId={id}
                    platform={report?.platform?.name ?? report?.platform}
                    existingPostCount={report?.posts?.length ?? 0}
                    existingPosts={report?.posts ?? []}
                    onSuccess={() => {
                        setAddPostsOpen(false);
                        queryClient.invalidateQueries({ queryKey: ['reportDetails', id] });
                    }}
                />

                <CampaignPickerModal
                    open={campaignPickerOpen}
                    onClose={() => setCampaignPickerOpen(false)}
                    onSelect={handleAttachToCampaign}
                />

                <AttachResultModal
                    open={!!attachResult}
                    onClose={() => setAttachResult(null)}
                    campaignName={attachResult?.campaignName}
                    result={attachResult?.result}
                    onGoToQueue={() => {
                        const campaignId = attachResult?.campaignId;
                        setAttachResult(null);
                        if (campaignId) navigate(`/payments/campaigns/details/${campaignId}`);
                    }}
                />

                {/* Errors only — successful attaches are shown in AttachResultModal above */}
                <Snackbar
                    open={!!attachToast}
                    autoHideDuration={7000}
                    onClose={() => setAttachToast(null)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                >
                    {attachToast && (
                        <Alert severity={attachToast.severity} onClose={() => setAttachToast(null)} sx={{ maxWidth: 480 }}>
                            {attachToast.message}
                        </Alert>
                    )}
                </Snackbar>

                {/* Errors only — a successful download just opens the file in a new tab */}
                <Snackbar
                    open={!!downloadToast}
                    autoHideDuration={7000}
                    onClose={() => setDownloadToast(null)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                >
                    {downloadToast && (
                        <Alert severity={downloadToast.severity} onClose={() => setDownloadToast(null)} sx={{ maxWidth: 480 }}>
                            {downloadToast.message}
                        </Alert>
                    )}
                </Snackbar>

                {/* Dark pill tab bar */}
                {!isPrintMode && (
                    <Box sx={{
                        display: 'flex', gap: 0.5,
                        bgcolor: C.surface,
                        border: `1px solid ${C.border}`,
                        borderRadius: 2, p: 0.75, mb: 3,
                        width: 'fit-content', flexWrap: 'wrap',
                        boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
                    }}>
                        {TAB_CONFIG.map(({ id: tabId, icon: Icon, label }) => (
                            <TabBtn key={tabId} id={tabId} icon={Icon} label={label} isActive={tab === tabId} onClick={handleTabChange} />
                        ))}
                    </Box>
                )}

                {isPrintMode ? (
                    <>
                        {TAB_CONFIG.map(({ id: tabId, Component, getProps }) => (
                            <Box key={tabId} sx={tabId > 0 ? { breakBefore: 'page' } : undefined}>
                                <Suspense fallback={null}>
                                    <Component {...getProps(enrichedReport, stats, snapshots, () => {})} isPublic={isPublicView} onBudgetSave={handleBudgetSave} onInfluencerBudgetSave={handleInfluencerBudgetSave} onInfluencerZoneSave={handleInfluencerZoneSave} onInfluencerRetailerCodeSave={handleInfluencerRetailerCodeSave} onPostSharesSave={handlePostSharesSave} brandName={enrichedReport?.brand?.name} onDeletePost={handleDeletePost} onCommentsLoaded={handleCommentsLoaded} printMode />
                                </Suspense>
                            </Box>
                        ))}
                        {printReady && <div data-pdf-ready="true" style={{ display: 'none' }} />}
                    </>
                ) : (
                    <AnimatePresence mode="wait">
                        {TAB_CONFIG.map(({ id: tabId, Component, getProps }) =>
                            tab === tabId && (
                                <motion.div
                                    key={tabId}
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -6 }}
                                    transition={{ duration: 0.22, ease: 'easeOut' }}
                                    style={{ width: '100%', minWidth: 0 }}
                                >
                                    <Suspense fallback={<Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}><CircularProgress size={40} sx={{ color: C.orange }} /></Box>}>
                                        <Component {...getProps(enrichedReport, stats, snapshots, handleTabChange)} isPublic={isPublicView} onBudgetSave={handleBudgetSave} onInfluencerBudgetSave={handleInfluencerBudgetSave} onInfluencerZoneSave={handleInfluencerZoneSave} onInfluencerRetailerCodeSave={handleInfluencerRetailerCodeSave} onPostSharesSave={handlePostSharesSave} brandName={enrichedReport?.brand?.name} onDeletePost={handleDeletePost} onCommentsLoaded={handleCommentsLoaded} />
                                    </Suspense>
                                </motion.div>
                            )
                        )}
                    </AnimatePresence>
                )}
                </Box>{/* /zIndex:1 content wrapper */}
            </Box>
        </>
    );
};

const CampaignReportDashboardWithBoundary = (props) => (
    <ReportErrorBoundary>
        <CampaignReportDashboard {...props} />
    </ReportErrorBoundary>
);

// Named exports so ReportGroupDashboard.jsx can build the same influencer-row /
// stat shape for its merged "Overview" tab (mergedInfluencers/mergedTotals)
// without duplicating this mapping logic.
export { mapInfluencerForTable, proxyImgUrl, fmt };
export default CampaignReportDashboardWithBoundary;
